// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DeviceBreakdown
 * @package @geenius/analytics/react
 * @description Renders the React device-breakdown visualization that compares
 * desktop, tablet, and mobile traffic. The component pairs a proportional bar
 * with a simple legend for quick distribution scans.
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
} from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";

function labelProgressBar(node: HTMLDivElement | null, label: string): void {
  node
    ?.querySelector('[role="progressbar"]')
    ?.setAttribute("aria-label", label);
}

/**
 * Props for rendering the React device-breakdown widget.
 *
 * @property breakdown Device counts grouped by desktop, tablet, and mobile.
 * @property className Optional class name merged into the wrapper.
 */
export interface DeviceBreakdownProps {
  breakdown: { desktop: number; tablet: number; mobile: number };
  className?: string;
}

/**
 * Renders the React device-breakdown widget.
 *
 * @param props Device counts and optional wrapper className.
 * @returns A device distribution widget for analytics dashboards.
 */
export function DeviceBreakdown({
  breakdown,
  className,
}: DeviceBreakdownProps): React.ReactElement {
  const total = breakdown.desktop + breakdown.tablet + breakdown.mobile || 1;
  const pct = (value: number): string =>
    `${((value / total) * 100).toFixed(1)}%`;
  const copy = useAnalyticsCopy();

  const segments = [
    {
      device: "desktop",
      value: breakdown.desktop,
      swatch: "bg-primary",
    },
    {
      device: "tablet",
      value: breakdown.tablet,
      swatch: "bg-accent-foreground",
    },
    {
      device: "mobile",
      value: breakdown.mobile,
      swatch: "bg-muted-foreground",
    },
  ];

  return (
    <Card className={className}>
      <CardHeader className="pb-0">
        <CardTitle className="text-sm" data-analytics-section-title>
          {copy.t("analytics.devices.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 pt-4" data-analytics-device-breakdown>
        {segments.map((segment) => (
          <div
            key={segment.device}
            className="grid gap-2"
            data-analytics-device-item
            data-device={segment.device}
          >
            <div className="flex items-center justify-between gap-3">
              <Badge variant="secondary">
                <span
                  className={cn("h-2.5 w-2.5 rounded-full", segment.swatch)}
                  aria-hidden="true"
                />
                {copy.deviceLabel(segment.device)}
              </Badge>
              <span
                className="text-sm font-medium text-muted-foreground"
                data-analytics-device-pct
              >
                {pct(segment.value)}
              </span>
            </div>
            <div
              ref={(node) =>
                labelProgressBar(
                  node,
                  `${copy.deviceLabel(segment.device)} traffic share`,
                )
              }
            >
              <Progress
                value={segment.value}
                max={total}
                data-analytics-device-segment
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
